import { useCallback, useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bank,
  CaretDown,
  CheckCircle,
  Gear,
  Question,
  SignOut,
  SpeakerHigh,
  SpeakerSlash,
  Ticket,
  Timer,
  Trophy,
  User as UserIcon,
  Wallet
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { API } from '@/App';
import { trackTicketPaymentIntent, trackTicketPurchased } from '@/analytics';
import TicketCounter from '@/components/TicketCounter';
import LiveTicketFeed from '@/components/LiveTicketFeed';
import WelcomeOnboardingModal from '@/components/WelcomeOnboardingModal';
import VerificationWizard from '@/components/VerificationWizard';
import PaymentNetworkPicker from '@/components/PaymentNetworkPicker';
import BrandLogo from '@/components/BrandLogo';
import TicketRevealCard from '@/components/TicketRevealCard';
import { useUnlockableAudio } from '@/hooks/useUnlockableAudio';
import { getPolicyLinks } from '@/content/policies';
import { useLanguage } from '@/i18n/LanguageContext';
import { getT } from '@/i18n/translations';

const Dashboard = ({ user, token, onLogout, onUpdateUser, isMuted, toggleMute }) => {
  const { language } = useLanguage();
  const t = getT('dashboard', language);
  const [activeDraw, setActiveDraw] = useState(null);
  const [draws, setDraws] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [currentUser, setCurrentUser] = useState(user);
  const [loading, setLoading] = useState(false);
  const { unlocked: audioUnlocked, unlockNow: unlockAudio } = useUnlockableAudio(
    'https://customer-assets.emergentagent.com/job_chance-games-7/artifacts/ka82katn_InspectorJ%20-%20Human%20Body%20-%20Heartbeat.wav',
    { loop: true, baseVolume: 0.84375, isMuted }
  );

  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('150');

  const [showTicketAnimation, setShowTicketAnimation] = useState(false);
  const [ticketAnimData, setTicketAnimData] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  // KYC gate modal — opens VerificationWizard when an unverified user tries to buy a ticket.
  const [showKycGate, setShowKycGate] = useState(false);

  const readJsonOnce = useCallback(async (response) => {
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [activeRes, drawsRes, ticketsRes, transRes, userRes, withdrawalsRes] = await Promise.all([
        fetch(`${API}/draws/active`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/draws`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/tickets/my-tickets`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/wallet/withdrawals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (activeRes.ok) setActiveDraw(await activeRes.json());
      if (drawsRes.ok) setDraws(await drawsRes.json());
      if (ticketsRes.ok) setMyTickets(await ticketsRes.json());
      if (transRes.ok) setTransactions(await transRes.json());
      if (withdrawalsRes.ok) setWithdrawals(await withdrawalsRes.json());
      if (userRes.ok) setCurrentUser(await userRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const syncTicketPaymentStatus = useCallback(async (intentId) => {
    try {
      const response = await fetch(`${API}/tickets/payments/${intentId}/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await readJsonOnce(response);
      if (!response.ok) {
        toast.error(data.detail || t.syncFailed);
        return;
      }
      if (data.ticket) {
        // Build a short verification code from the ticket UUID (first 8 hex chars, uppercased)
        const rawId = String(data.ticket.id || '').replace(/-/g, '');
        const ticketCode = rawId.slice(0, 8).toUpperCase();
        setTicketAnimData({
          ticketNumber: data.ticket.ticketNumber,
          ticketCode,
          drawNumber: data.paymentIntent?.drawNumber || activeDraw?.drawNumber,
        });
        setShowTicketAnimation(true);
        // GA4: revenue tracking on confirmed purchase
        trackTicketPurchased(
          data.paymentIntent?.amount || 150,
          data.paymentIntent?.currency || 'EUR',
          data.paymentIntent?.drawNumber || activeDraw?.drawNumber || 1,
        );
        try {
          const fire = (opts) => confetti({
            particleCount: 60,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#cba135', '#e6be4b', '#a8872b', '#fff5d0'],
            ...opts,
          });
          fire();
          setTimeout(() => fire({ particleCount: 40, angle: 60, spread: 55, origin: { x: 0, y: 0.7 } }), 200);
          setTimeout(() => fire({ particleCount: 40, angle: 120, spread: 55, origin: { x: 1, y: 0.7 } }), 400);
        } catch (confettiErr) {
          // canvas-confetti can fail in some headless/test envs; non-fatal
          console.warn('Confetti animation failed:', confettiErr);
        }
        toast.success(t.ticketIssued);
      } else {
        toast.info(`Ticket payment status: ${data.paymentIntent?.status || 'pending'}`);
      }
      if (data.user) setCurrentUser(data.user);
      fetchData();
      onUpdateUser();
    } catch (syncErr) {
      console.warn('Ticket payment sync failed:', syncErr);
      toast.error(t.syncFailed);
    }
  }, [activeDraw?.drawNumber, fetchData, onUpdateUser, readJsonOnce, token, t]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const intentId = params.get('intent');
    const ticketPaymentStatus = params.get('ticketPayment');

    if (ticketPaymentStatus === 'success') {
      toast.info(t.paymentReturned);
      if (intentId) syncTicketPaymentStatus(intentId);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    if (ticketPaymentStatus === 'cancelled') {
      toast.error(t.paymentCancelled);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [syncTicketPaymentStatus]);

  const handleRequestWithdrawal = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/wallet/withdrawals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ amount: parseFloat(withdrawalAmount) })
      });
      const data = await readJsonOnce(response);
      if (response.ok) {
        toast.success(t.withdrawalCreated);
        if (data.user) setCurrentUser(data.user);
        setShowWithdrawal(false);
        setWithdrawalAmount('150');
        fetchData();
        onUpdateUser();
      } else {
        toast.error(data.detail || t.withdrawalFailed);
      }
    } catch (withdrawErr) {
      console.warn('Withdrawal request failed:', withdrawErr);
      toast.error(t.withdrawalFailed);
    }
    setLoading(false);
  };

  const handleEnterDraw = async () => {
    if (!activeDraw) {
      toast.error(t.noActiveDraw);
      return;
    }
    // KYC gate: returning customers can deposit / browse, but cannot enter a draw
    // until their KYC has been approved by admin. We open the verification wizard
    // straight away so the user can finish in the same session.
    const kycApproved =
      currentUser?.kycReviewStatus === 'approved' ||
      ['verified', 'active'].includes(currentUser?.verificationStatus);
    if (!kycApproved) {
      toast.info('Identity verification is required to enter the draw.', {
        duration: 6000,
        style: { background: '#1a1508', border: '1px solid rgba(203,161,53,0.3)', color: '#cba135' }
      });
      setShowKycGate(true);
      return;
    }
    if (userHasTicket) {
      toast('You already have a ticket for this draw. Wait for the next round.', {
        duration: 4000,
        style: { background: '#1a1508', border: '1px solid rgba(203,161,53,0.3)', color: '#cba135' }
      });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/tickets/payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ drawId: activeDraw.id, network: selectedNetwork })
      });

      const data = await readJsonOnce(response);
      if (response.ok) {
        const intent = data.paymentIntent;
        trackTicketPaymentIntent(selectedNetwork, intent?.providerCurrency);
        if (intent?.provider === 'mock_payram') {
          toast.error('PayRam checkout is not configured yet. Set PayRam env before public ticket sales.');
          fetchData();
          onUpdateUser();
          setLoading(false);
          return;
        }

        if (intent?.checkoutUrl) {
          setLoading(false);
          window.location.assign(intent.checkoutUrl);
          return;
        }

        toast.info(t.invoiceCreated);
        fetchData();
      } else {
        const detail = typeof data?.detail === 'string' ? data.detail : 'Failed to create ticket invoice';
        toast.error(detail);
      }
    } catch (enterDrawErr) {
      console.warn('Buy ticket failed:', enterDrawErr);
      toast.error('Failed to create ticket invoice');
    }
    setLoading(false);
  };

  const userHasTicket = myTickets.some((ticket) => ticket.drawId === activeDraw?.id);

  return (
    <div className="min-h-screen bg-[#0e0b04]">
      <header className="bg-[#0e0b04]/70 backdrop-blur-xl border-b border-[#cba135]/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <BrandLogo />

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm tracking-wide text-[#cba135]/70">{t.walletBalance}</p>
              <p data-testid="wallet-balance" className="text-xl font-semibold tabular-nums text-[#cba135]">
                EUR {(currentUser?.walletBalance || 0).toFixed(2)}
              </p>
              {(currentUser?.lockedBalance || 0) > 0 && (
                <p className="text-xs text-[#cba135]/50">
                  {t.locked}: EUR {(currentUser?.lockedBalance || 0).toFixed(2)}
                </p>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="profile-menu-trigger"
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#1a1508] border border-[#cba135]/20 hover:border-[#cba135]/50 hover:bg-[#cba135]/5 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#cba135] to-[#a8872b] flex items-center justify-center text-[#0e0b04] font-bold text-sm">
                    {(currentUser?.firstName || currentUser?.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden md:inline text-sm text-[#cba135] max-w-[120px] truncate">
                    {currentUser?.firstName || currentUser?.name}
                  </span>
                  <CaretDown size={14} className="text-[#cba135]/60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-64 bg-[#1a1508] border-[#cba135]/20 text-[#cba135]"
                align="end"
                data-testid="profile-menu-content"
              >
                <DropdownMenuLabel className="text-[#cba135]/60 font-normal text-xs uppercase tracking-wider">
                  {currentUser?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#cba135]/10" />
                <div className="sm:hidden px-2 py-1.5">
                  <p className="text-xs text-[#cba135]/60">{t.walletBalance}</p>
                  <p className="text-base font-semibold tabular-nums">
                    EUR {(currentUser?.walletBalance || 0).toFixed(2)}
                  </p>
                </div>
                <DropdownMenuSeparator className="bg-[#cba135]/10 sm:hidden" />
                <DropdownMenuItem
                  onClick={() => { unlockAudio(); toggleMute(); }}
                  data-testid="profile-menu-mute"
                  className="cursor-pointer hover:bg-[#cba135]/10 focus:bg-[#cba135]/10"
                >
                  {isMuted ? <SpeakerSlash size={16} weight="fill" className="mr-2" /> : <SpeakerHigh size={16} weight="fill" className="mr-2" />}
                  {isMuted ? (t.unmute || 'Unmute sound') : (t.mute || 'Mute sound')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#cba135]/10" />
                <DropdownMenuItem
                  onClick={onLogout}
                  data-testid="profile-menu-logout"
                  className="cursor-pointer text-red-400 hover:bg-red-500/10 focus:bg-red-500/10 hover:text-red-300 focus:text-red-300"
                >
                  <SignOut size={16} className="mr-2" />
                  {t.logout || 'Sign out'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h2 className="heading-font text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#cba135] mb-2">
            {t.welcome} <span className="gold-shimmer">{currentUser?.name}</span>
          </h2>
          <p className="text-base text-[#cba135]/70">{t.liveDesc}</p>
        </motion.div>

        <LiveTicketFeed />

        {activeDraw && (() => {
          const sold = Number(activeDraw.ticketsSold || 0);
          const max = Number(activeDraw.maxTickets || 2000);
          const remaining = Math.max(0, max - sold);
          const pct = max > 0 ? Math.min(100, (sold / max) * 100) : 0;
          return (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              data-testid="fomo-banner"
              className="mb-6 bg-gradient-to-r from-[#1a1508] via-[#241a08] to-[#1a1508] border border-[#cba135]/15 rounded-lg px-5 py-3 flex items-center gap-4 overflow-hidden relative"
            >
              <div className="flex items-center gap-2 shrink-0">
                <Timer size={20} weight="duotone" className="text-[#cba135]" />
                <span className="heading-font text-2xl font-semibold tabular-nums text-[#cba135]" data-testid="fomo-remaining">
                  {remaining.toLocaleString('en-US')}
                </span>
              </div>
              <p className="text-sm text-[#cba135]/70 leading-tight flex-1 min-w-0">
                {t.ticketsRemainingFomo}
              </p>
              <div className="flex flex-col items-end shrink-0 min-w-[64px] sm:min-w-[120px]">
                <span className="text-[9px] sm:text-[10px] uppercase tracking-wider text-[#cba135]/50">{t.fillProgress}</span>
                <span className="text-xs sm:text-sm font-semibold text-[#cba135] tabular-nums">{pct.toFixed(1)}%</span>
              </div>
              <div className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-[#cba135] to-[#e6be4b]" style={{ width: `${pct}%` }} />
            </motion.div>
          );
        })()}

        {activeDraw && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
            <div className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-8">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135] mb-2">
                    Draw #{activeDraw.drawNumber}
                  </h3>
                  <p className="text-sm text-[#cba135]/70">{t.activeNow}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#cba135]/70 mb-1">{t.prizePool}</p>
                  <p
                    className="heading-font text-3xl font-semibold gold-shimmer"
                    data-testid="active-draw-jackpot"
                  >
                    EUR {Number(activeDraw.prizes?.first || 200000).toLocaleString('en-US')}
                  </p>
                  <p className="text-xs text-[#cba135]/60 mt-1">
                    {t.guaranteedFirstPrize || 'Guaranteed first prize'}
                  </p>
                  <p className="text-xs text-[#cba135]/40 mt-0.5">
                    + {(activeDraw.prizes?.secondWinners || 20)} × EUR {Number(activeDraw.prizes?.second || 1000).toLocaleString('en-US')}
                  </p>
                </div>
              </div>

              <TicketCounter drawId={activeDraw.id} />

              {!userHasTicket && (
                <div className="mt-8">
                  <PaymentNetworkPicker value={selectedNetwork} onChange={setSelectedNetwork} />
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="request-withdrawal-button"
                  onClick={() => setShowWithdrawal(true)}
                  variant="outline"
                  className="border-[#cba135] text-[#cba135] hover:bg-[#cba135]/10 flex-1"
                >
                  <Bank size={20} className="mr-2" />
                  {t.withdraw}
                </Button>

                <Button
                  data-testid="buy-ticket-button"
                  onClick={handleEnterDraw}
                  disabled={loading || userHasTicket}
                  className={`bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold flex-1 ${!userHasTicket && !loading ? 'shadow-[0_0_24px_rgba(203,161,53,0.45)] animate-pulse-gold' : ''}`}
                >
                  <Ticket size={20} className="mr-2" />
                  {loading ? t.preparingInvoice : userHasTicket ? t.ticketPurchased : `${t.buyTicket} (EUR ${activeDraw.ticketPrice})`}
                </Button>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-[#cba135]/45">
                {t.ticketNote}
              </p>

              {(() => {
                const kycApproved =
                  currentUser?.kycReviewStatus === 'approved' ||
                  ['verified', 'active'].includes(currentUser?.verificationStatus);
                if (kycApproved || userHasTicket) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setShowKycGate(true)}
                    data-testid="dashboard-complete-verification-cta"
                    className="mt-3 w-full text-left bg-[#cba135]/8 border border-[#cba135]/25 hover:border-[#cba135]/55 rounded-md px-3 py-2.5 text-xs text-[#e6be4b] transition-colors"
                  >
                    <span className="font-semibold">Identity check pending.</span> Complete verification to enter the draw →
                  </button>
                );
              })()}

              {userHasTicket && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="ticket-purchased-message"
                  className="mt-4 bg-[#cba135]/10 border border-[#cba135]/20 rounded-lg p-4 flex items-center gap-3"
                >
                  <Ticket size={24} weight="duotone" className="text-[#cba135]" />
                  <span className="text-[#cba135]">{t.ticketGoodLuck}</span>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        <Tabs defaultValue="draws" className="mb-12">
          <TabsList className="bg-[#1a1508] border border-[#cba135]/10">
            <TabsTrigger data-testid="draws-tab" value="draws" className="data-[state=active]:bg-[#cba135]/10 data-[state=active]:text-[#cba135]">{t.allDraws}</TabsTrigger>
            <TabsTrigger data-testid="my-tickets-tab" value="tickets" className="data-[state=active]:bg-[#cba135]/10 data-[state=active]:text-[#cba135]">{t.myTickets}</TabsTrigger>
            <TabsTrigger data-testid="transactions-tab" value="transactions" className="data-[state=active]:bg-[#cba135]/10 data-[state=active]:text-[#cba135]">{t.transactions}</TabsTrigger>
            <TabsTrigger data-testid="withdrawals-tab" value="withdrawals" className="data-[state=active]:bg-[#cba135]/10 data-[state=active]:text-[#cba135]">{t.withdrawals}</TabsTrigger>
          </TabsList>

          <TabsContent value="draws">
            <div className="space-y-4">
              {draws.length === 0 ? (
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
                  <p className="text-sm text-[#cba135]/70">{t.noDraws}</p>
                </div>
              ) : draws.map((draw) => (
                <motion.div key={draw.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <h4 className="heading-font text-lg font-medium text-[#cba135] mb-1">
                        Draw #{draw.drawNumber}
                      </h4>
                      <p className="text-sm text-[#cba135]/70">
                        {draw.status === 'active' ? t.active : t.completed} - {draw.ticketsSold || 0} / {draw.maxTickets || 2000} {t.tickets}
                      </p>
                    </div>
                    {draw.status === 'completed' && draw.winners && (
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-1">{t.winners}</p>
                        <div className="text-sm text-[#cba135]">
                          {myTickets.some((ticket) => ticket.drawId === draw.id && ticket.userId === draw.winners?.firstPrize?.userId) && (
                            <p className="font-bold gold-shimmer">
                              You won EUR {Number(draw.winners.firstPrize.amount || 0).toLocaleString()}
                            </p>
                          )}
                          {draw.winners.secondPrize?.some((winner) => myTickets.some((ticket) => ticket.drawId === draw.id && ticket.userId === winner.userId)) && (
                            <p className="font-bold text-[#cba135]">
                              You won EUR {Number(draw.winners.secondPrize?.[0]?.amount || 0).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tickets">
            <div className="space-y-4">
              {myTickets.length === 0 ? (
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
                  <p className="text-sm text-[#cba135]/70">{t.noTickets}</p>
                </div>
              ) : myTickets.map((ticket) => {
                const draw = draws.find((item) => item.id === ticket.drawId);
                return (
                  <motion.div key={ticket.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <Ticket size={32} weight="duotone" className="text-[#cba135]" />
                      <div>
                        <p className="heading-font text-lg text-[#cba135]">Ticket #{ticket.ticketNumber}</p>
                        <p className="text-sm text-[#cba135]/70">Draw #{draw?.drawNumber || '-'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">
                        {draw?.status === 'active' ? t.pending : t.completed}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            <div className="space-y-4">
              {transactions.length === 0 ? (
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
                  <p className="text-sm text-[#cba135]/70">{t.noTransactions}</p>
                </div>
              ) : transactions.map((trans) => (
                <motion.div key={trans.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6 flex justify-between items-center gap-4">
                  <div className="flex items-center gap-4">
                    {trans.type === 'win' ? <Trophy size={32} weight="duotone" className="text-[#cba135]" /> :
                      trans.type === 'deposit' || trans.type === 'internal_credit' ? <Wallet size={32} weight="duotone" className="text-[#cba135]" /> :
                        trans.type?.includes('withdrawal') ? <Bank size={32} weight="duotone" className="text-[#cba135]" /> :
                          <Ticket size={32} weight="duotone" className="text-[#cba135]" />}
                    <div>
                      <p className="text-[#cba135]">{trans.description}</p>
                      <p className="text-sm text-[#cba135]/70">{new Date(trans.timestamp).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <p className={`text-lg font-semibold ${trans.amount > 0 ? 'text-green-500' : 'text-[#cba135]'}`}>
                    {trans.amount > 0 ? '+' : ''}EUR {Math.abs(trans.amount || 0).toFixed(2)}
                  </p>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="withdrawals">
            <div className="space-y-4">
              {withdrawals.length === 0 ? (
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
                  <p className="text-sm text-[#cba135]/70">{t.noWithdrawals}</p>
                </div>
              ) : withdrawals.map((withdrawal) => (
                <motion.div key={withdrawal.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <Bank size={32} weight="duotone" className="text-[#cba135]" />
                    <div>
                      <p className="text-[#cba135]">{t.withdrawalTo} {withdrawal.bankAccountIban}</p>
                      <p className="text-sm text-[#cba135]/70">
                        {new Date(withdrawal.requestedAt).toLocaleDateString()} - {withdrawal.status}
                      </p>
                      {withdrawal.reviewReason && <p className="text-xs text-[#cba135]/50">{withdrawal.reviewReason}</p>}
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-[#cba135]">
                    EUR {Number(withdrawal.amount || 0).toFixed(2)}
                  </p>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <footer className="pb-10 text-center" data-testid="dashboard-legal-footer">
          <nav className="mb-3 flex flex-wrap justify-center gap-3" aria-label="Legal links">
            {getPolicyLinks(language).map((link) => (
              <a
                key={link.slug}
                href={`/legal/${link.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-semibold uppercase text-[#cba135]/50 transition-colors hover:text-[#cba135]"
              >
                {link.title}
              </a>
            ))}
          </nav>
          <p className="text-xs text-[#cba135]/35">
            {t.ledgerFooter}
          </p>
        </footer>
      </main>

      <Dialog open={showWithdrawal} onOpenChange={setShowWithdrawal}>
        <DialogContent className="bg-[#0e0b04] border border-[#cba135]/20 backdrop-blur-2xl max-w-md" data-testid="withdrawal-dialog">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl text-[#cba135] flex items-center gap-2">
              <Bank size={24} weight="duotone" />
              {t.requestWithdrawal}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRequestWithdrawal} className="space-y-5 mt-4">
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#cba135]/70">{t.available}</span>
                <span className="text-[#cba135]">EUR {(currentUser?.walletBalance || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#cba135]/70">{t.locked}</span>
                <span className="text-[#cba135]">EUR {(currentUser?.lockedBalance || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#cba135]/70">{t.iban}</span>
                <span className="text-[#cba135] text-right break-all">{currentUser?.bankAccountIban || '-'}</span>
              </div>
            </div>
            <div>
              <Label className="text-[#cba135] text-sm">{t.amount}</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#cba135]/50 text-sm font-semibold">EUR</span>
                <Input
                  data-testid="withdrawal-amount-input"
                  type="number"
                  step="0.01"
                  min="10"
                  max={currentUser?.walletBalance || 0}
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135] pl-12 text-2xl font-semibold h-14"
                  required
                />
              </div>
            </div>
            <Button data-testid="confirm-withdrawal-button" type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold h-12">
              {loading ? t.creatingRequest : t.createWithdrawal}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <TicketRevealCard
        open={showTicketAnimation}
        data={ticketAnimData}
        onClose={() => {
          setShowTicketAnimation(false);
          if (ticketAnimData) {
            toast.success(`Ticket #${ticketAnimData.ticketNumber} entered into Draw #${ticketAnimData.drawNumber}`);
          }
          setTicketAnimData(null);
        }}
      />

      <WelcomeOnboardingModal user={currentUser} />

      {/* KYC gate — opens when a non-verified user tries to enter the draw. */}
      <Dialog open={showKycGate} onOpenChange={setShowKycGate}>
        <DialogContent className="bg-[#0e0b04] border-[#cba135]/20 max-w-md" data-testid="dashboard-kyc-gate-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#cba135]">Complete verification to enter the draw</DialogTitle>
          </DialogHeader>
          <VerificationWizard
            token={token}
            language={language}
            onComplete={async () => {
              setShowKycGate(false);
              // Refresh user data so the gate re-evaluates with the new KYC status.
              await onUpdateUser();
              await fetchData();
              toast.success('Verification submitted. Your draw entry will unlock as soon as admin approves it.', {
                duration: 6000,
              });
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
